import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_auto_fix_high } from 'react-icons-kit/md/ic_auto_fix_high';
import { ic_schedule } from 'react-icons-kit/md/ic_schedule';
import { ic_exposure } from 'react-icons-kit/md/ic_exposure';
import { ic_format_quote } from 'react-icons-kit/md/ic_format_quote';
import { ic_functions } from 'react-icons-kit/md/ic_functions';
import { ic_call_split } from 'react-icons-kit/md/ic_call_split';
import { ic_security } from 'react-icons-kit/md/ic_security';
import { ic_swap_horiz } from 'react-icons-kit/md/ic_swap_horiz';
import { ic_call_merge } from 'react-icons-kit/md/ic_call_merge';
import { inputClasses } from 'components/common/tailwindClassnames';
import { HoverTooltip } from 'components/common/HoverTooltip';
import { Transform, TransformKind, ValueMapPair } from './types';
import {
  TRANSFORM_ADD_BTN,
  TRANSFORM_ADD_BTN_ACTIVE,
  TRANSFORM_ADD_BTN_DISABLED,
  TRANSFORM_PILL,
  TRANSFORM_PREVIEW,
  TRANSFORM_MENU,
  TRANSFORM_MENU_SECTION,
  TRANSFORM_MENU_ITEM,
} from './styles';

// ── Transform catalog ──
// Metadata drives both the picker and the pill labels. `jq` is the canonical
// hint shown on the right of a picker row; `groupKey` groups the menu.

type TransformDef = {
  kind: TransformKind;
  labelKey: string; // user-facing name (i18n key)
  jq: string; // short jq hint shown in the picker
  groupKey: string; // section heading (i18n key)
  hasArg: boolean; // whether the pill expands to an argument editor
  icon: any; // react-icons-kit glyph shown in the picker row and pill
  descriptionKey: string; // one-line "what it does" (i18n key), shown in the row tooltip
  example: string; // concrete before → after sample (literal data, untranslated)
};

export const TRANSFORM_DEFS: TransformDef[] = [
  {
    kind: 'tonumber',
    labelKey: 'To number',
    jq: 'tonumber',
    groupKey: 'Type & dates',
    hasArg: false,
    icon: ic_exposure,
    descriptionKey: 'Converts text to a number.',
    example: '"-27.57" → -27.57',
  },
  {
    kind: 'tostring',
    labelKey: 'To text',
    jq: 'tostring',
    groupKey: 'Type & dates',
    hasArg: false,
    icon: ic_format_quote,
    descriptionKey: 'Converts a value to text.',
    example: '359441 → "359441"',
  },
  {
    kind: 'epoch_iso_s',
    labelKey: 'Epoch → ISO (s)',
    jq: 'todate',
    groupKey: 'Type & dates',
    hasArg: false,
    icon: ic_schedule,
    descriptionKey: 'Converts a seconds timestamp to an ISO date.',
    example: '1719403200 → "2024-06-26T12:00:00Z"',
  },
  {
    kind: 'epoch_iso_ms',
    labelKey: 'Epoch → ISO (ms)',
    jq: '/1000 | todate',
    groupKey: 'Type & dates',
    hasArg: false,
    icon: ic_schedule,
    descriptionKey: 'Converts a milliseconds timestamp to an ISO date.',
    example: '1719403200000 → "2024-06-26T12:00:00Z"',
  },
  {
    kind: 'divide',
    labelKey: 'Divide',
    jq: '. / N',
    groupKey: 'Math & text',
    hasArg: true,
    icon: ic_functions,
    descriptionKey: 'Divides the value by a number you set.',
    example: '-1234567 ÷ 1e6 → -1.234567',
  },
  {
    kind: 'split',
    labelKey: 'Split',
    jq: 'split',
    groupKey: 'Math & text',
    hasArg: true,
    icon: ic_call_split,
    descriptionKey: 'Splits text into a list on a delimiter.',
    example: '"-27.5,31.6" → ["-27.5","31.6"]',
  },
  {
    kind: 'default',
    labelKey: 'Default if empty',
    jq: '// value',
    groupKey: 'Value',
    hasArg: true,
    icon: ic_security,
    descriptionKey: 'Uses a value you set when the field is missing.',
    example: 'null → "unknown"',
  },
  {
    kind: 'value_map',
    labelKey: 'Value map',
    jq: 'lookup',
    groupKey: 'Value',
    hasArg: true,
    icon: ic_swap_horiz,
    descriptionKey: 'Replaces listed values with new ones.',
    example: '"ENTERED" → "ew_entered"',
  },
  {
    kind: 'coalesce',
    labelKey: 'Fallback field',
    jq: '// .path',
    groupKey: 'Value',
    hasArg: true,
    icon: ic_call_merge,
    descriptionKey: 'Reads another field when this one is empty.',
    example: 'null → .cached.lat',
  },
];

const DEF_BY_KIND: Record<TransformKind, TransformDef> = TRANSFORM_DEFS.reduce(
  (acc, d) => {
    acc[d.kind] = d;
    return acc;
  },
  {} as Record<TransformKind, TransformDef>,
);

const GROUP_ORDER = ['Type & dates', 'Math & text', 'Value'];

/** Short summary shown inside a pill, including the argument when set. */
function pillSummary(t: Transform): string {
  switch (t.kind) {
    case 'divide':
      return `÷ ${t.arg || 'N'}`;
    case 'split':
      return `split ${JSON.stringify(t.arg ?? '')}`;
    case 'default':
      return `default ${t.arg ? JSON.stringify(t.arg) : '…'}`;
    case 'coalesce':
      return `↦ ${t.arg || '.path'}`;
    // Count unique non-empty "from" keys — what the emitted jq object actually holds
    // (duplicates are last-wins), so the count survives a save/reload unchanged.
    case 'value_map':
      return `map (${new Set((t.pairs ?? []).filter((p) => p.from).map((p) => p.from)).size})`;
    default:
      return DEF_BY_KIND[t.kind]?.jq ?? t.kind;
  }
}

function transformHasArg(kind: TransformKind): boolean {
  return DEF_BY_KIND[kind]?.hasArg ?? false;
}

// ── Picker popover ──

// Flat option order (matches the rendered group order) for keyboard navigation.
const FLAT_DEFS: TransformDef[] = GROUP_ORDER.flatMap((group) =>
  TRANSFORM_DEFS.filter((d) => d.groupKey === group),
);

export function TransformAddButton({
  onAdd,
  disabled,
  allowedKinds,
}: {
  onAdd: (t: Transform) => void;
  // Shown-but-disabled (with an explanatory tooltip) instead of hidden, so the feature
  // stays discoverable on filters where transform edits can't be saved (complex jq —
  // the builder never regenerates a partially-parsed filter).
  disabled?: boolean;
  // When set, the menu only offers these kinds — an unmapped slot has a null base, so
  // only default/coalesce make sense until a coalesce (or a mapping) provides a value.
  allowedKinds?: TransformKind[];
}) {
  const { t } = useTranslation();
  const defs = allowedKinds
    ? FLAT_DEFS.filter((d) => allowedKinds.includes(d.kind))
    : FLAT_DEFS;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Two-pass positioning: the menu renders hidden off-screen first so its real height is
  // measurable, then flips above the button when there's no room below and clamps to the
  // viewport — otherwise the bottom group is unreachable for rows near the viewport edge.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const fitsBelow = r.bottom + 4 + menuHeight <= window.innerHeight - 8;
    const top = fitsBelow ? r.bottom + 4 : Math.max(8, r.top - 4 - menuHeight);
    setPos({ top, left: Math.max(8, r.right - 260) });
  }, [open]);

  // Move DOM focus to the active option so keyboard users see the highlight.
  useEffect(() => {
    if (open && pos) optionRefs.current[activeIndex]?.focus();
  }, [open, pos, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target))
        return;
      setOpen(false);
    };
    // Close on outside scroll/resize (repositioning while scrolling is fragile) — but a
    // scroll INSIDE the menu must not close it, or its overflow content is unreachable.
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const pick = (kind: TransformKind) => {
    onAdd(
      kind === 'value_map' ? { kind, pairs: [{ from: '', to: '' }] } : { kind },
    );
    setOpen(false);
    btnRef.current?.focus();
  };

  // Portal events bubble through the React tree, so this wrapper handler covers
  // both the trigger button and the portaled menu items.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (
        e.key === 'ArrowDown' ||
        e.key === 'ArrowUp' ||
        e.key === 'Enter' ||
        e.key === ' '
      ) {
        e.preventDefault();
        setActiveIndex(0);
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        btnRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % defs.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + defs.length) % defs.length);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(defs.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        pick(defs[activeIndex].kind);
        break;
      default:
        break;
    }
  };

  if (disabled) {
    return (
      <HoverTooltip
        content={t(
          "Transforms can't be added while the filter has complex expressions. Edit the filter in Code mode.",
        )}
        position="top-right"
        align="left"
        ariaLabel={t('Add transform')}
        className="shrink-0"
        // The wand sits inside the mapping chip, which is overflow-hidden — the
        // tooltip must escape via a portal or it renders clipped/invisible.
        portal
      >
        {/* A span, not a disabled <button> — disabled buttons swallow the hover
                    events the tooltip wrapper needs. */}
        <span className={TRANSFORM_ADD_BTN_DISABLED} aria-disabled="true">
          <Icon icon={ic_auto_fix_high} size={18} />
        </span>
      </HoverTooltip>
    );
  }

  return (
    <span onKeyDown={onKeyDown}>
      <HoverTooltip
        content={t('Add transform')}
        position="top"
        focusable={false}
        portal
      >
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={open ? TRANSFORM_ADD_BTN_ACTIVE : TRANSFORM_ADD_BTN}
          aria-label={t('Add transform')}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Icon icon={ic_auto_fix_high} size={18} />
        </button>
      </HoverTooltip>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: 260,
              zIndex: 60,
              maxHeight: 'min(70vh, 480px)',
              overflowY: 'auto',
              visibility: pos ? 'visible' : 'hidden',
            }}
            className={TRANSFORM_MENU}
          >
            {GROUP_ORDER.filter((group) =>
              defs.some((d) => d.groupKey === group),
            ).map((group) => (
              <div key={group}>
                <div className={TRANSFORM_MENU_SECTION}>{t(group as any)}</div>
                {defs
                  .filter((d) => d.groupKey === group)
                  .map((d) => {
                    const flatIndex = defs.indexOf(d);
                    return (
                      <HoverTooltip
                        key={d.kind}
                        // Portaled: the menu is a scroll container (overflow-y auto),
                        // which would clip an in-flow tooltip at its edges.
                        portal
                        // Hover-intent: without a delay the tooltip pops the moment
                        // the menu opens (items auto-focus) or the pointer sweeps by.
                        delayMs={1000}
                        // Full menu: Value group sits at the bottom near the viewport
                        // edge, so its tooltips flip up. Filtered menu (unmapped slot)
                        // is short and sits right under the wand — a top tooltip would
                        // overlap the menu and drop zone, so everything points down.
                        position={
                          !allowedKinds && group === 'Value' ? 'top' : 'bottom'
                        }
                        align="left"
                        focusable={false}
                        className="w-full"
                        content={
                          <span className="block text-xs">
                            {t(d.descriptionKey as any)}
                            <code className="mt-1 block font-mono text-[11px] text-ui-text-primary-text">
                              {d.example}
                            </code>
                          </span>
                        }
                      >
                        <button
                          ref={(el) => (optionRefs.current[flatIndex] = el)}
                          type="button"
                          role="menuitem"
                          onClick={() => pick(d.kind)}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          className={`${TRANSFORM_MENU_ITEM} ${flatIndex === activeIndex ? 'bg-unlisted-light-purple' : ''}`}
                        >
                          <span className="flex items-center gap-2">
                            <Icon
                              icon={d.icon}
                              size={14}
                              className="shrink-0 text-unlisted-purple"
                            />
                            {t(d.labelKey as any)}
                          </span>
                          <span className="font-mono text-[10px] text-ui-text-primary-text">
                            {d.jq}
                          </span>
                        </button>
                      </HoverTooltip>
                    );
                  })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </span>
  );
}

// ── Applied-transform pills + argument editors ──

export function TransformPills({
  transforms,
  onChange,
  readOnly,
  previewText,
}: {
  transforms: Transform[];
  onChange: (next: Transform[]) => void;
  readOnly?: boolean;
  // Value after all transforms are applied, shown after the pills (`= 2024-06-26T…`)
  // so the row reads raw-in-chip → transformed-after-pills.
  previewText?: string;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<number | null>(null);

  const update = (index: number, next: Transform) =>
    onChange(transforms.map((x, i) => (i === index ? next : x)));
  const remove = (index: number) => {
    onChange(transforms.filter((_, i) => i !== index));
    setEditing(null);
  };

  return (
    <div className="ml-4 mt-1.5 flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-ui-text-primary-text" aria-hidden="true">
          ↳
        </span>
        {transforms.map((tr, i) => {
          const canEdit = !readOnly && transformHasArg(tr.kind);
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <span
                  className="text-[10px] text-ui-text-primary-text"
                  aria-hidden="true"
                >
                  ›
                </span>
              )}
              <span className={TRANSFORM_PILL}>
                <Icon
                  icon={DEF_BY_KIND[tr.kind]?.icon ?? ic_auto_fix_high}
                  size={12}
                  className="shrink-0 text-unlisted-purple"
                />
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() =>
                    canEdit && setEditing(editing === i ? null : i)
                  }
                  className={canEdit ? 'cursor-pointer' : 'cursor-default'}
                  aria-label={canEdit ? t('Edit transform') : undefined}
                >
                  {canEdit ? (
                    <HoverTooltip
                      content={t('Edit transform')}
                      position="top"
                      focusable={false}
                      portal
                    >
                      {pillSummary(tr)}
                    </HoverTooltip>
                  ) : (
                    pillSummary(tr)
                  )}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="leading-none text-unlisted-purple hover:text-unlisted-dark-purple"
                    aria-label={t('Remove transform')}
                  >
                    ×
                  </button>
                )}
              </span>
            </React.Fragment>
          );
        })}
        {previewText !== undefined && (
          <span className={TRANSFORM_PREVIEW}>= {previewText}</span>
        )}
      </div>
      {editing !== null &&
        transforms[editing] &&
        transformHasArg(transforms[editing].kind) && (
          <ArgEditor
            transform={transforms[editing]}
            onChange={(next) => update(editing, next)}
          />
        )}
    </div>
  );
}

function ArgEditor({
  transform,
  onChange,
}: {
  transform: Transform;
  onChange: (next: Transform) => void;
}) {
  const { t } = useTranslation();
  const setArg = (arg: string) => onChange({ ...transform, arg });

  if (transform.kind === 'value_map') {
    const pairs = transform.pairs ?? [];
    const setPairs = (next: ValueMapPair[]) =>
      onChange({ ...transform, pairs: next });
    return (
      <div className="ml-8 flex w-fit flex-col gap-1 rounded border border-ui-divider-lines bg-unlisted-very-light-gray p-1.5">
        {pairs.map((p, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              value={p.from}
              onChange={(e) =>
                setPairs(
                  pairs.map((x, j) =>
                    j === i ? { ...x, from: e.target.value } : x,
                  ),
                )
              }
              placeholder={t('from')}
              className={`${inputClasses.standard} w-28 font-mono text-xs`}
            />
            <span className="text-xs text-ui-text-primary-text">→</span>
            <input
              value={p.to}
              onChange={(e) =>
                setPairs(
                  pairs.map((x, j) =>
                    j === i ? { ...x, to: e.target.value } : x,
                  ),
                )
              }
              placeholder={t('to')}
              className={`${inputClasses.standard} w-28 font-mono text-xs`}
            />
            <button
              type="button"
              onClick={() => setPairs(pairs.filter((_, j) => j !== i))}
              className="text-base leading-none text-ui-text-secondary-text hover:text-ui-text-primary-text"
              aria-label={t('Remove')}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setPairs([...pairs, { from: '', to: '' }])}
          className="self-start text-xs text-ui-cta-primary hover:underline"
        >
          {t('Add pair')}
        </button>
      </div>
    );
  }

  const placeholder =
    transform.kind === 'divide'
      ? t('divisor, e.g. 1000000')
      : transform.kind === 'split'
        ? t('delimiter, e.g. ,')
        : transform.kind === 'coalesce'
          ? t('source path, e.g. cached.lat')
          : t('default value');

  // Compact inline input anchored under the pill — a full-width boxed editor
  // dominates the row and reads as a separate form.
  return (
    <div className="ml-8">
      <input
        value={transform.arg ?? ''}
        onChange={(e) => setArg(e.target.value)}
        placeholder={placeholder}
        className={`${inputClasses.standard} w-48 font-mono text-xs`}
        type={transform.kind === 'divide' ? 'number' : 'text'}
        autoFocus
      />
    </div>
  );
}
